import { useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  openAuthModal,
  closeAuthModal,
  setFormPageChanged,
  clearAlertMessage,
} from '../store/ui/actions';
import { RootState } from '../store';
import { RouteContext } from '../context';
import * as Constants from '../constants';
import { AUTH_STATES } from '../constants/authConsts';

import history from '../history';
import { setSelectedItemAction } from '../store/reduxTypes';
import { UrlUtils } from '../utils';

const useNavigate = () => {
  const nextPath = useSelector((state: RootState) => state.ui.nextPath);
  const isFormPageChanged = useSelector(
    (state: RootState) => state.ui.isFormPageChanged,
  );

  const { isMobile, pathname, query } = useContext(RouteContext);
  const dispatch = useDispatch();

  const openDesktopOnlyModal = () =>
    dispatch(openAuthModal(AUTH_STATES.DESKTOP_ONLY_MODAL));

  const handleRouting = (path: string) => {
    if (isMobile && Constants.ONLY_DESKTOP_SUPPORT_PAGES.has(path)) {
      openDesktopOnlyModal();
    } else {
      history.push(path);
    }
  };

  const handleClose = () => {
    dispatch(closeAuthModal());
  };

  const clearPendingStates = () => {
    dispatch(setSelectedItemAction(''));
    dispatch(clearAlertMessage());
  };

  const navigate = (path: string) => {
    // determine the full current path by combining the pathname and query string
    let currentPath = `${pathname}`;
    const queryString = UrlUtils.FormatUrlQueryToString(query);
    if (queryString) {
      currentPath += `?${queryString}`;
    }

    if (path === currentPath) {
      // when path is not changed do nothing
      return;
    }

    if (isFormPageChanged) {
      dispatch(openAuthModal(AUTH_STATES.CONFIRM_PAGE_CHANGE_MODAL, path));
      return;
    }

    // clear any pending states
    clearPendingStates();

    handleRouting(path);
  };

  const navigateBack = () => {
    history.goBack();
  };

  /**
   * use history.push to navigate to the next path and clear pending state if form changed
   * @param path where to navigate to
   */
  const forceNavigate = (path: string) => {
    if (isFormPageChanged) {
      dispatch(setFormPageChanged(false));
    }
    handleRouting(path);
  };

  const handleConfirm = () => {
    if (isFormPageChanged) {
      dispatch(setFormPageChanged(false));
    }
    handleRouting(nextPath);

    handleClose();
  };

  return {
    nextPath,
    navigate,
    handleClose,
    handleConfirm,
    navigateBack,
    forceNavigate,
  };
};

export default useNavigate;
